import {
  Component,
  OnInit,
  Input, SimpleChanges, OnChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '../../../../shared';
import { Property, PropertyType } from '../../shared/models';
import { Workspace } from '../../../shared';
import { UserProfile } from '../../../user/models';

@Component({
  selector: 'sp-add-property-details',
  templateUrl: './add-property-details.html',
  standalone: false
})
export class AddPropertyDetailsComponent implements OnInit, OnChanges {

  @Input() formGroup: UntypedFormGroup;
  @Input() property: Property;
  @Input() propertyTypes: PropertyType[];
  @Input() workspaces: Workspace[];
  @Input() currentWorkspace: Workspace;
  @Input() userProfile: UserProfile;
  @Input() states: string[];
  @Input() currentWorkspaceId: string;

  isCondoConverted = false;
  currentYear: number;
  zipCode = new Subject();
  sortedWorkspaces: Workspace[];

  constructor(private fb: UntypedFormBuilder) { }

  ngOnInit() {

    this.initForm();
    this.currentYear = new Date().getFullYear();
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes['currentWorkspace']) {
      if (this.currentWorkspace && this.currentWorkspace.workspaceId && this.formGroup.controls['workspaceId']) {
        this.formGroup.controls['workspaceId'].setValue(this.currentWorkspace.workspaceId);
      }
    }
    if (changes['workspaces'] && this.workspaces != null) {
      this.setsortedWorkspaces();
    }
  }

  setsortedWorkspaces() {
    // sort by name
    this.sortedWorkspaces = this.workspaces.sort((a, b) => {
      const nameA = a.companyName == null ? a.name : a.companyName;
      const nameB = b.companyName == null ? b.name : b.companyName;
      if (nameA.toLowerCase() < nameB.toLowerCase()) {
        return -1;
      }
      if (nameA.toLowerCase() > nameB.toLowerCase()) {
        return 1;
      }

      // names must be equal
      return 0;
    });
  }

  resetForm() {
    this.formGroup.reset();
    this.formGroup.controls['workspaceId'].setValue(this.currentWorkspace.workspaceId);
    this.formGroup.controls['state'].setValue('AL');
    this.formGroup.controls['conversionYesNo'].setValue(this.property.isCondoConverted);
    this.formGroup.controls['isTest'].setValue(this.property.isCondoConverted);
  }

  private initForm() {

    this.formGroup.addControl('workspaceId',
      this.fb.control(this.currentWorkspace ? this.currentWorkspace.workspaceId : '')
    );

    this.formGroup.addControl('isTest', this.fb.control(false));

    // If there is > 1 workspace, add a validator
    if (this.workspaces && this.workspaces.length > 1) {
      this.formGroup.controls['workspaceId'].setValidators(Validators.compose([
        Validators.required,
        CustomValidators.notEmptyStringValidator,
      ]));
    }

    this.formGroup.addControl(
      'name',
      this.fb.control(this.property.name,
        [
          Validators.required,
          CustomValidators.notEmptyStringValidator,
          CustomValidators.allowedCharsStringValidator2,
        ])
    );

    this.formGroup.addControl(
      'address1',
      this.fb.control(this.property.addressLine1,
        [
          Validators.required,
          CustomValidators.notEmptyStringValidator,
          CustomValidators.allowedCharsStringValidator2,
        ])
    );

    this.formGroup.addControl(
      'address2',
      this.fb.control(this.property.addressLine2,
        [
          CustomValidators.notEmptyStringValidator,
          CustomValidators.allowedCharsStringValidator2,
        ])
    );

    this.formGroup.addControl(
      'city',
      this.fb.control(this.property.city,
        [
          Validators.required,
          CustomValidators.notEmptyStringValidator,
          CustomValidators.allowedCharsStringValidator2,
        ])
    );

    this.formGroup.addControl(
      'state',
      this.fb.control(this.property.state,
        [
          Validators.required,
          CustomValidators.allowedOnlyCharsStringValidator,
          CustomValidators.notEmptyStringValidator,
        ])
    );
    if (this.formGroup.controls['state'] !== undefined) {
      this.formGroup.controls['state'].setValue('AL');
    }


    this.formGroup.addControl(
      'zipCode',
      this.fb.control(this.property.zipCode,
        [
          Validators.required,
          CustomValidators.zipcodeValidator
        ])
    );

    this.formGroup.addControl(
      'yearBuilt',
      this.fb.control(this.property.yearBuilt,
        [
          Validators.required,
          CustomValidators.yearValidator,
          CustomValidators.rangeValidator(1700, this.currentYear,
            (min, max) => `Property year built should be between ${min} and ${max}`)
        ])
    );
    this.formGroup.controls['yearBuilt'].valueChanges.subscribe(() => {
      this.setYearOfConversionValidators();
    });

    this.formGroup.addControl(
      'propertyTypeId',
      this.fb.control(this.property.propertyTypeId,
        [
          Validators.required,
          CustomValidators.positiveNumber,
        ])
    );

    this.formGroup.addControl(
      'numberOfUnits',
      this.fb.control(this.property.numberOfUnits,
        [
          Validators.required,
          CustomValidators.noDecimalPointNumber,
          CustomValidators.positiveNumber,
          CustomValidators.rangeValidator(1, 99999, (min, max) => `Number of units should be between ${min} and ${max}.`)
        ])
    );

    this.formGroup.addControl(
      'numberOfFloors',
      this.fb.control(this.property.numberOfFloors, [])
    );

    this.formGroup.addControl(
      'conversionYesNo',
      this.fb.control(this.property.isCondoConverted,
        [
          Validators.required,
        ])
    );
    this.formGroup.controls['conversionYesNo'].valueChanges.subscribe(() => {
      this.setYearOfConversionValidators();
    });

    this.formGroup.addControl(
      'yearOfConversion',
      this.fb.control(this.property.yearOfConversion)
    );

    this.formGroup.addControl(
      'propertyDescription',
      this.fb.control(this.property.propertyDescription)
    );
  }

  private setYearOfConversionValidators() {

    const yearOfConversion = this.formGroup.controls['yearOfConversion'];
    const yearBuilt = this.formGroup.controls['yearBuilt'];
    const conversionYesNo = this.formGroup.controls['conversionYesNo'];

    yearOfConversion.clearValidators();

    if (conversionYesNo.value === false) {
      yearOfConversion.reset();
      return;
    }

    const buildYear = yearBuilt.value;
    if (yearBuilt.valid) {

      const validationMessage = Number(buildYear) === this.currentYear
        ? `Year of Conversion must be ${this.currentYear}.`
        : `Year of Conversion must be between ${buildYear} and ${this.currentYear}.`;

      yearOfConversion.setValidators((Validators.compose([
        Validators.required,
        CustomValidators.yearValidator,
        CustomValidators.rangeValidator(buildYear, this.currentYear,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (min, max) => validationMessage)
      ])));
    } else {
      yearOfConversion.setValidators((Validators.compose([Validators.required])));
    }

    yearOfConversion.updateValueAndValidity();
  }
}
